import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, PackageOpen } from 'lucide-react';
import { Card, CardContent } from '@components/Card';
import { DashboardRecentMaterialReceipt, DashboardRecentMaterialReceipts } from '@/types/dashboard';
import { formatDateBR } from '@pages/financial/financialUtils';

/** 0 = Em conferência (âmbar); demais situações em verde. */
const statusBadgeClass = (status: number): string =>
  status === 0
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';

const partyTypeLabel = (t: DashboardRecentMaterialReceipt['party_type']): string =>
  t === 'customer' ? 'Cliente' : t === 'provider' ? 'Fornecedor' : '';

/**
 * Painel "Recebimentos de materiais (últimos 5 dias)" (admin/finance).
 * Cada linha abre o recebimento; o rodapé leva à lista completa.
 */
export const RecentMaterialReceiptsPanel: React.FC<{ data: DashboardRecentMaterialReceipts }> = ({ data }) => (
  <Card>
    <CardContent className="p-0">
      <div className="p-4 border-b border-slate-200 flex items-start gap-3">
        <PackageOpen className="text-slate-500 flex-shrink-0 mt-0.5" size={20} />
        <p className="text-sm text-slate-700">
          {data.items.length === 0
            ? `Nenhum recebimento desde ${formatDateBR(data.date_from)}.`
            : `${data.items.length} recebimento(s) desde ${formatDateBR(data.date_from)}`}
        </p>
      </div>

      {data.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left font-medium px-4 py-2">Data</th>
                <th className="text-left font-medium px-4 py-2">Cliente / Fornecedor</th>
                <th className="text-left font-medium px-4 py-2">Situação</th>
                <th className="text-right font-medium px-4 py-2">Itens</th>
                <th className="text-right font-medium px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((r) => (
                <tr key={r.material_receipt_id} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                    {r.receipt_date ? formatDateBR(r.receipt_date) : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <Link to={`/material-receipts/${r.material_receipt_id}`} className="text-slate-800 hover:underline">
                      {r.party_name || '—'}
                    </Link>
                    {partyTypeLabel(r.party_type) && (
                      <span className="text-slate-500 text-xs ml-2">{partyTypeLabel(r.party_type)}</span>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${statusBadgeClass(r.status)}`}
                    >
                      {r.status_label}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right text-slate-700">{r.items_count}</td>
                  <td className="px-4 py-2 text-right">
                    <Link
                      to={`/material-receipts/${r.material_receipt_id}`}
                      className="inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
                    >
                      #{r.material_receipt_id}
                      <ArrowRight size={14} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4 py-3 border-t border-slate-200">
        <Link to="/material-receipts" className="text-sm font-medium text-blue-700 hover:underline">
          Ver todos →
        </Link>
      </div>
    </CardContent>
  </Card>
);
